/**
 * PersonChip — ein Namens-Chip für ein Mitglied an einer Fahrt.
 *
 * Farbe/Icon folgen `STATUS_META`. Der angezeigten Person (eigene oder die im
 * Admin-Modus bearbeitete) wird `variant="filled"` gegeben, anderen
 * `outlined`. Vom Admin gesetzte Einträge tragen ein kleines
 * `SupervisorAccount`-Icon (NICHT ein Schloss — ausdrücklicher Nutzerwunsch).
 * Mitglieder ohne Eintrag erscheinen als gestrichelter „Name · Offen"-Chip.
 */

import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import SupervisorAccount from "@mui/icons-material/SupervisorAccount";
import { useI18n } from "../../i18n/I18nProvider";
import { STATUS_META, type AvailabilityStatus } from "../../theme/status";

export default function PersonChip({
  name,
  status,
  highlighted,
  setByAdmin,
  onClick,
}: {
  name: string;
  /** Status der Person oder null → gestrichelter „Offen"-Chip. */
  status: AvailabilityStatus | null;
  /** Hervorgehobene (gefüllte) Darstellung für die angezeigte Person. */
  highlighted?: boolean;
  /** Vom Admin gesetzt → SupervisorAccount-Marker. */
  setByAdmin?: boolean;
  onClick?: () => void;
}) {
  const { t } = useI18n();

  // Kein Eintrag: gestrichelter, ungefüllter Chip „Name · Offen" ohne Icon.
  if (status === null) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={`${name} · ${t("weeklyPlan.status.open")}`}
        onClick={onClick}
        sx={{ borderStyle: "dashed" }}
      />
    );
  }

  const meta = STATUS_META[status];
  const Icon = meta.icon;

  const label = setByAdmin ? (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      {name}
      <SupervisorAccount
        titleAccess={t("weeklyPlan.admin.lockAria")}
        sx={{ fontSize: 15 }}
      />
    </Box>
  ) : (
    name
  );

  return (
    <Chip
      size="small"
      icon={<Icon />}
      label={label}
      color={meta.color}
      variant={highlighted ? "filled" : "outlined"}
      onClick={onClick}
    />
  );
}
